import { createSlice } from '@reduxjs/toolkit';
import {
  createDialogue,
  getDialogue,
  getMessages,
  getSessions,
  updateDialogue,
  updateSupportDialogue,
} from './operations';

const initialState = {
  channels: [],
  messages: [],
  currentChat: null,
  isLoading: false,
  error: null,
  dialogueUsers: [],
  dialogueUsersIds: [],
  messagesPagination: { page: 1 },
  dialoguesPagination: {
    page: 1,
    perPage: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  sessions: {},
};

const handlePending = state => {
  state.isLoading = true;
  state.error = null;
};

const handleRejected = (state, { payload }) => {
  state.isLoading = false;
  state.error = payload;
};

export const sliceLiveChat = createSlice({
  name: 'LiveChat',
  initialState,
  reducers: {
    setDialoguePage: (state, { payload: page }) => {
      state.dialoguesPagination.page = page;
    },
    setDialogueUsersIds: (state, { payload: users }) => {
      state.dialogueUsersIds = users;
    },
    addDialogueUsersId: (state, { payload: user }) => {
      if (user?.length) {
        state.dialogueUsersIds.push(...user);
      } else {
        state.dialogueUsersIds.push(user);
      }
      state.dialogueUsersIds = [...new Set(state.dialogueUsersIds)];
    },
    clearMessages: state => {
      state.messages = [];
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    removeMessage: (state, { payload }) => {
      state.messages = state.messages.filter(el => el._id !== payload);
    },
    selectChat: (state, action) => {
      const dialogue = action.payload;
      state.currentChat = action.payload;
      if (!state.sessions[dialogue?._id] && dialogue?._id) {
        state.sessions[dialogue?._id] = {
          chatId: dialogue?._id,
          counter: 60,
          freeUserId: dialogue.freeUserId,
          lastMessage: dialogue.lastMessage,
        };
      }
    },
    updateSession: (state, action) => {
      const { chatId, ...params } = action.payload;
      if (state.sessions[chatId]) {
        state.sessions[chatId] = { ...state.sessions[chatId], ...params };
      }
    },
    setDialogue: (state, { payload: userData }) => {
      const index = state.channels.findIndex(el => el._id === userData._id);
      if (index >= 0) {
        state.channels[index] = { ...state.channels[index], ...userData };
      }
    },
    incrementUnReadMessage: (state, { payload: chatId }) => {
      const index = state.channels.findIndex(el => el._id === chatId);
      if (index >= 0) {
        const chat = state.channels[index];
        chat.unRead.freeUser += 1;
        chat.unRead.paidUser += 1;
      }
    },
    setChannels: (state, action) => {
      state.channels = action.payload;
    },
    updateMessage: (state, { payload: message }) => {
      const array = state.messages;
      for (let i = 0; i < array.length; i++) {
        if (array[i]._id === message._id) {
          array[i] = { ...array[i], ...message };
          break;
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDialogue.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;

        const incoming = payload.data;
        console.log('incoming', incoming);

        const existingIds = new Set(state.channels.map(d => d._id));

        const merged =
          payload.pagination.page === 1
            ? incoming
            : [
                ...state.channels,
                ...incoming.filter(d => !existingIds.has(d._id)),
              ];

        state.channels = merged;
        state.dialogueUsers = [
          ...state.dialogueUsers,
          ...payload.dialogueUsers.filter(
            user => !state.dialogueUsers.some(u => u._id === user._id),
          ),
        ];
        state.dialoguesPagination = payload.pagination;

        for (const el of incoming) {
          if (!state.sessions[el?._id] && el?._id) {
            state.sessions[el?._id] = {
              chatId: el?._id,
              counter: 60,
              freeUserId: el.freeUserId,
              lastMessage: el.lastMessage,
            };
          }
        }
      })
      .addCase(updateDialogue.fulfilled, (state, { payload: userData }) => {
        const index = state.channels.findIndex(el => el._id === userData._id);
        state.channels[index] = { ...state.channels[index], ...userData };
        if (state.currentChat?._id === userData?._id) {
          state.currentChat = { ...state.currentChat, ...userData };
        }
      })
      .addCase(
        updateSupportDialogue.fulfilled,
        (state, { payload: userData }) => {
          const index = state.channels.findIndex(el => el._id === userData._id);
          state.channels[index] = { ...state.channels[index], ...userData };
        },
      )
      .addCase(getSessions.fulfilled, (state, { payload: userData }) => {
        state.sessions = userData;
      })
      .addCase(getDialogue.pending, handlePending)
      .addCase(getDialogue.rejected, handleRejected)
      .addCase(getMessages.fulfilled, (state, { payload: userData }) => {
        state.isLoading = false;
        state.error = null;
        const { messages, ...pagination } = userData;
        state.messagesPagination = pagination;
        if (pagination.page === 1) {
          state.messages = messages.reverse();
        } else {
          state.messages.unshift(...messages.reverse());
        }
      })
      .addCase(getMessages.pending, handlePending)
      .addCase(getMessages.rejected, handleRejected)
      .addCase(createDialogue.fulfilled, (state, { payload: userData }) => {
        state.dialogueUsersIds.push(userData.paidUserId, userData.freeUserId);
        state.isLoading = false;
        state.error = null;
        const exists = state.channels.find(d => d._id === userData._id);
        if (!exists) state.channels.unshift(userData);
      })
      .addCase(createDialogue.pending, handlePending)
      .addCase(createDialogue.rejected, handleRejected);
  },
});

export const {
  addMessage,
  selectChat,
  clearMessages,
  updateMessage,
  updateSession,
  setDialogue,
  setChannels,
  incrementUnReadMessage,
  removeMessage,
  setDialogueUsersIds,
  addDialogueUsersId,
} = sliceLiveChat.actions;
export default sliceLiveChat.reducer;
